import { useState, useEffect } from 'react';
import api from '../config/axiosConfig';

/**
 * Hook to fetch ALL items from a paginated API endpoint
 * Useful for dropdowns/selects where you need all available options
 * 
 * @param {string} url - The API endpoint URL
 * @param {object} options - Additional options (params, skip)
 * @returns {object} { data: array of all items, loading, error }
 */
export default function useFetchAll(url, options = {}) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const { params = {}, skip = false } = options;

    useEffect(() => {
        if (skip) {
            setLoading(false);
            return;
        }

        let isMounted = true;

        const fetchAllPages = async () => {
            setLoading(true);
            setError(null);

            try {
                const allResults = [];
                let page = 1;
                let hasMore = true;
                const pageSize = 100; // Fetch in chunks of 100

                while (hasMore && isMounted) {
                    const response = await api.get(url, {
                        params: {
                            ...params,
                            page,
                            page_size: pageSize
                        }
                    });

                    const results = response.data?.results || [];
                    allResults.push(...results);

                    // Check if there are more pages
                    const count = response.data?.count || 0;
                    hasMore = allResults.length < count;
                    page++;
                }

                if (isMounted) {
                    setData(allResults);
                    setLoading(false);
                }
            } catch (err) {
                if (isMounted) {
                    setError(err);
                    setLoading(false);
                }
            }
        };

        fetchAllPages();

        return () => {
            isMounted = false;
        };
    }, [url, params, skip]);

    return { data, loading, error };
}

